import React, { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { Tool, ToolInstance } from '@/types';

interface ToolModalProps {
  isOpen: boolean;
  onClose: () => void;
  tools: Tool[];
  onAddTool: (tool: ToolInstance) => void;
}

export function ToolModal({ isOpen, onClose, tools, onAddTool }: ToolModalProps) {
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [parameters, setParameters] = useState<Record<string, any>>({});

  const handleToolSelect = (tool: Tool) => {
    setSelectedTool(tool);
    const initialParams: Record<string, any> = {};
    tool.parameters.forEach(param => {
      initialParams[param.name] = param.default || '';
    });
    setParameters(initialParams);
  };

  const handleParameterChange = (paramName: string, value: any) => {
    setParameters(prev => ({
      ...prev,
      [paramName]: value,
    }));
  };

  const handleAddTool = () => {
    if (!selectedTool) return;

    onAddTool({
      name: selectedTool.name,
      parameters,
    });

    // Reset form
    setSelectedTool(null);
    setParameters({});
    onClose();
  };

  const handleCancel = () => {
    setSelectedTool(null);
    setParameters({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title="Configure Tool" size="lg">
      <div className="space-y-6">
        {/* Tool Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Tool
          </label>
          <select
            value={selectedTool?.name || ''}
            onChange={(e) => {
              const tool = tools.find(t => t.name === e.target.value);
              if (tool) handleToolSelect(tool);
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Choose a tool...</option>
            {tools.map((tool) => (
              <option key={tool.name} value={tool.name}>
                {tool.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tool Description */}
        {selectedTool && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="font-medium text-blue-900 mb-2">{selectedTool.name}</h4>
            <p className="text-blue-700 text-sm">{selectedTool.description}</p>
          </div>
        )}

        {/* Tool Parameters */}
        {selectedTool && selectedTool.parameters.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-4">Parameters</h4>
            <div className="space-y-4">
              {selectedTool.parameters.map((param) => (
                <div key={param.name} className="border border-gray-200 rounded-md p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      {param.name}
                    </label>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs bg-gray-200 px-2 py-1 rounded font-mono">
                        {param.type}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        param.required 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {param.required ? 'Required' : 'Optional'}
                      </span>
                    </div>
                  </div>
                  
                  {param.type === 'boolean' ? (
                    <input
                      type="checkbox"
                      checked={parameters[param.name] || false}
                      onChange={(e) => handleParameterChange(param.name, e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                  ) : (
                    <input
                      type={param.type === 'number' ? 'number' : 'text'}
                      value={parameters[param.name] || ''}
                      onChange={(e) => handleParameterChange(param.name, 
                        param.type === 'number' ? Number(e.target.value) : e.target.value
                      )}
                      placeholder={param.default?.toString() || ''}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                  
                  {param.description && (
                    <p className="text-xs text-gray-500 mt-1">{param.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAddTool}
            disabled={!selectedTool}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Tool
          </button>
        </div>
      </div>
    </Modal>
  );
}